import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { 
  Visibility as ViewIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OrganisationMenu, MenuStatus, Comment } from '../../services/interfaces';
import { organisationService } from '../../services/serviceFactory';
import { SelectChangeEvent } from '@mui/material';

const MenuManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menus, setMenus] = useState<OrganisationMenu[]>([]);
  const [filteredMenus, setFilteredMenus] = useState<OrganisationMenu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMenuDialogOpen, setViewMenuDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<OrganisationMenu | null>(null);
  const [comment, setComment] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [statusFilter, setStatusFilter] = useState<MenuStatus | 'all'>('all');

  useEffect(() => {
    fetchMenus();
  }, []);

  useEffect(() => {
    filterMenus();
  }, [menus, statusFilter]);

  const fetchMenus = async () => {
    try {
      setLoading(true);
      const data = await organisationService.getMenus();
      setMenus(data);
    } catch (err) {
      console.error('Failed to fetch menus:', err);
      setError(t('menu.errorLoadingMenus'));
    } finally {
      setLoading(false);
    }
  };

  const filterMenus = () => {
    if (statusFilter === 'all') {
      setFilteredMenus(menus);
    } else {
      setFilteredMenus(menus.filter(menu => 
        typeof menu.status === 'number' 
          ? menu.status === MenuStatus[statusFilter] 
          : menu.status === statusFilter
      ));
    }
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value as MenuStatus | 'all');
  };

  const handleViewMenu = (menu: OrganisationMenu) => {
    setSelectedMenu(menu);
    setViewMenuDialogOpen(true);
  };

  const handleOpenApproveDialog = (menu: OrganisationMenu) => {
    setSelectedMenu(menu);
    setComment('');
    setApproveDialogOpen(true);
  };

  const handleOpenRejectDialog = (menu: OrganisationMenu) => {
    setSelectedMenu(menu);
    setComment('');
    setRejectDialogOpen(true);
  };  const handleApproveMenu = async () => {
    if (!selectedMenu) return;
    
    try {
      setLoading(true);
      
      // Update the menu status with the comment included in the DTO
      const updatedMenu = {
        ...selectedMenu,
        status: MenuStatus.Approved
      };
      
      // Call the API to update the menu status, passing the comment directly
      await organisationService.updateMenuStatus(updatedMenu, comment.trim() || undefined);
      
      setApproveDialogOpen(false);
      fetchMenus(); // Refresh menu list
    } catch (err) {
      console.error('Failed to approve menu:', err);
      setError(t('menu.errorApprovingMenu'));
    } finally {
      setLoading(false);
    }
  };  const handleRejectMenu = async () => {
    if (!selectedMenu) return;
    
    try {
      setLoading(true);

      // For rejection, a comment is required
      if (!comment.trim()) {
        // Show error if no comment is provided for rejection
        setError(t('menu.commentRequired'));
        setLoading(false);
        return;
      }

      // Update the menu status
      const updatedMenu = {
        ...selectedMenu,
        status: MenuStatus.Rejected
      };
      
      // Call the API to update the menu status, passing the comment directly
      await organisationService.updateMenuStatus(updatedMenu, comment.trim());
      
      setRejectDialogOpen(false);
      fetchMenus(); // Refresh menu list
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Failed to reject menu:', err);
      setError(t('menu.errorRejectingMenu'));
    } finally {
      setLoading(false);
    }
  };

  const getMenuStatusFromNumber = (value: number | any): MenuStatus => {
    switch(value) {
      case 0: return MenuStatus.Draft;
      case 1: return MenuStatus.Submitted;
      case 2: return MenuStatus.UpdateRequested;
      case 3: return MenuStatus.Updated;
      case 4: return MenuStatus.Approved;
      case 5: return MenuStatus.Rejected;
      case 6: return MenuStatus.Active;
      case 7: return MenuStatus.Inactive;
      default: return MenuStatus.Draft; // default fallback
    }
  };

  const getStatusChipColor = (status: MenuStatus | any) => {
    const statusValue = typeof status === 'number' 
    ? getMenuStatusFromNumber(status)
    : status;
      
    switch (statusValue) {
      case MenuStatus.Draft:
        return 'default';
      case MenuStatus.Submitted:
      case MenuStatus.UpdateRequested:
      case MenuStatus.Updated:
        return 'warning';
      case MenuStatus.Approved:
      case MenuStatus.Active:
        return 'success';
      case MenuStatus.Rejected:
      case MenuStatus.Inactive:
        return 'error';
      default:
        return 'default';
    }
  };

  const isMenuActionable = (menu: OrganisationMenu) => {
    // Allow Draft, Submitted or Updated menus to be approved or rejected
    console.log(menu.status);
    return menu.status === MenuStatus.Draft || menu.status === MenuStatus.Submitted || menu.status === MenuStatus.Updated;
  };

  if (loading && menus.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('organisation.menuManagement')}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FilterIcon />}
          onClick={() => setShowFilter(!showFilter)}
        >
          {t('filter')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showFilter && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel>{t('menu.filterByStatus')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('menu.filterByStatus')}
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="all">{t('menu.status.all')}</MenuItem>
              <MenuItem value={MenuStatus.Draft}>{t('menu.status.Draft')}</MenuItem>
              <MenuItem value={MenuStatus.Submitted}>{t('menu.status.Submitted')}</MenuItem>
              <MenuItem value={MenuStatus.UpdateRequested}>{t('menu.status.UpdateRequested')}</MenuItem>
              <MenuItem value={MenuStatus.Updated}>{t('menu.status.Updated')}</MenuItem>
              <MenuItem value={MenuStatus.Approved}>{t('menu.status.Approved')}</MenuItem>
              <MenuItem value={MenuStatus.Rejected}>{t('menu.status.Rejected')}</MenuItem>
              <MenuItem value={MenuStatus.Active}>{t('menu.status.Active')}</MenuItem>
              <MenuItem value={MenuStatus.Inactive}>{t('menu.status.Inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('menu.name')}</TableCell>
              <TableCell>{t('menu.validFrom')}</TableCell>
              <TableCell>{t('menu.validTo')}</TableCell>
              <TableCell>{t('menu.status.root')}</TableCell>
              <TableCell>{t('menu.meals')}</TableCell>
              <TableCell>{t('menu.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMenus.map((menu) => (
              <TableRow key={menu.id}>
                <TableCell>{menu.name}</TableCell>
                <TableCell>
                  {new Date(menu.validFrom).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {new Date(menu.validTo).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={t(`menu.status.${menu.status}`)} 
                    color={getStatusChipColor(menu.status) as "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>{menu.meals.length}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex' }}>
                    <Tooltip title={t('menu.view')}>
                      <IconButton onClick={() => handleViewMenu(menu)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    {isMenuActionable(menu) && (
                      <>
                        <Tooltip title={t('menu.approve')}>
                          <IconButton 
                            onClick={() => handleOpenApproveDialog(menu)}
                            color="success"
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('menu.reject')}>
                          <IconButton 
                            onClick={() => handleOpenRejectDialog(menu)}
                            color="error"
                          >
                            <RejectIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredMenus.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('menu.noMenus')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Menu Dialog */}
      <Dialog open={viewMenuDialogOpen} onClose={() => setViewMenuDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedMenu?.name || t('menu.details')}
        </DialogTitle>
        <DialogContent>
          {selectedMenu && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">{t('menu.validFrom')}:</Typography>
                <Typography variant="body1">
                  {new Date(selectedMenu.validFrom).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">{t('menu.validTo')}:</Typography>
                <Typography variant="body1">
                  {new Date(selectedMenu.validTo).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">{t('menu.status.root')}:</Typography>
                <Chip 
                  label={t(`menu.status.${selectedMenu.status}`)} 
                  color={getStatusChipColor(selectedMenu.status) as "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"} 
                  size="small" 
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">{t('menu.description')}:</Typography>
                <Typography variant="body1">
                  {selectedMenu.description || t('menu.noDescription')}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  {t('menu.meals')}:
                </Typography>
                <Divider />
                
                {selectedMenu.meals.length === 0 ? (
                  <Typography variant="body1" sx={{ my: 2 }}>
                    {t('menu.noMeals')}
                  </Typography>
                ) : (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {selectedMenu.meals.map(meal => (
                      <Grid item xs={12} sm={6} md={4} key={meal.id}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              {meal.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {meal.description || t('menu.noDescription')}
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                              {meal.price.toLocaleString(undefined, { 
                                style: 'currency', 
                                currency: 'EUR' 
                              })}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  {t('menu.comments')}:
                </Typography>
                <Divider />
                
                {selectedMenu.comments.length === 0 ? (
                  <Typography variant="body1" sx={{ my: 2 }}>
                    {t('menu.noComments')}
                  </Typography>
                ) : (
                  <List>
                    {selectedMenu.comments.map(comment => (
                      <ListItem key={comment.id}>
                        <ListItemText
                          primary={comment.text}
                          secondary={`Version: ${comment.menuVersion}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewMenuDialogOpen(false)}>
            {t('close')}
          </Button>
          {selectedMenu && isMenuActionable(selectedMenu) && (
            <>
              <Button 
                onClick={() => {
                  setViewMenuDialogOpen(false);
                  handleOpenApproveDialog(selectedMenu);
                }}
                variant="contained"
                color="success"
              >
                {t('menu.approve')}
              </Button>
              <Button 
                onClick={() => {
                  setViewMenuDialogOpen(false);
                  handleOpenRejectDialog(selectedMenu);
                }}
                variant="contained"
                color="error"
              >
                {t('menu.reject')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Approve Menu Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>{t('menu.approveMenuTitle')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('menu.approveMenuConfirm', { name: selectedMenu?.name })}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="comment"
            label={t('menu.approvalComment')}
            fullWidth
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>
            {t('cancel')}
          </Button>
          <Button onClick={handleApproveMenu} color="success" variant="contained">
            {t('menu.approve')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Menu Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>{t('menu.rejectMenuTitle')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('menu.rejectMenuConfirm', { name: selectedMenu?.name })}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="comment"
            label={t('menu.rejectionComment')}
            fullWidth
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required
            error={!comment}
            helperText={!comment ? t('menu.commentRequired') : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleRejectMenu} 
            color="error" 
            variant="contained"
            disabled={!comment}
          >
            {t('menu.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuManagement;